import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { dashboardAPI, ownerWidgetsAPI } from '../../api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { format, addMonths } from 'date-fns';
import {
  Building2,
  Users,
  TrendingUp,
  Clock,
  AlertTriangle,
  ChevronRight,
  UserCheck,
  UserX,
  Calendar,
  Timer,
  DollarSign,
  ArrowUp,
  ArrowDown,
  AlertCircle,
  CheckCircle,
  XCircle,
  Hourglass
} from 'lucide-react';

const OwnerDashboard = () => {
  const [stats, setStats] = useState(null);
  const [widgets, setWidgets] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forecastPeriod, setForecastPeriod] = useState(format(addMonths(new Date(), 1), 'yyyy-MM'));

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, widgetsRes] = await Promise.all([
        dashboardAPI.getOwnerDashboard(),
        ownerWidgetsAPI.getWidgets()
      ]);
      setStats(dashRes.data);
      setWidgets(widgetsRes.data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const fetchForecast = useCallback(async () => {
    if (!forecastPeriod) return;
    try {
      const res = await ownerWidgetsAPI.getPayrollForecast(forecastPeriod);
      setForecast(res.data);
    } catch (err) {
      console.error('Failed to load forecast:', err);
    }
  }, [forecastPeriod]);

  useEffect(() => {
    fetchForecast();
  }, [fetchForecast]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount || 0);
  };

  if (loading) {
    return (
      <DashboardLayout title="Portfolio Overview">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="Portfolio Overview" 
      subtitle="Real-time insights across all your companies"
    >
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <Card className="stat-card" data-testid="total-companies-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Companies</p>
                <p className="stat-value">{stats?.total_companies || 0}</p>
              </div>
              <Building2 className="h-10 w-10 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="total-employees-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Total Employees</p>
                <p className="stat-value">{stats?.total_employees || 0}</p>
              </div>
              <Users className="h-10 w-10 text-primary opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="overall-attendance-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Overall Attendance</p>
                <p className="stat-value">{stats?.overall_attendance_rate || 0}%</p>
              </div>
              <TrendingUp className="h-10 w-10 text-emerald-500 opacity-20" />
            </div>
            <Progress 
              value={stats?.overall_attendance_rate || 0} 
              className="mt-3 h-2" 
            />
          </CardContent>
        </Card>

        <Card className="stat-card" data-testid="productivity-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="stat-label">Productivity Score</p>
                <p className="stat-value">{stats?.overall_productivity_score || 0}%</p>
              </div>
              <Clock className="h-10 w-10 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Cards - Bento Grid */}
      <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
        Companies
      </h2>
      
      {stats?.companies?.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No companies yet. Create your first company to get started.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {stats?.companies?.map((company) => (
            <Card 
              key={company.company_id} 
              className="bento-card hover:shadow-lg transition-all"
              data-testid={`company-card-${company.company_id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {company.company_name}
                  </CardTitle>
                  {company.red_flag_count > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      {company.red_flag_count}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Attendance Rate */}
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Attendance Rate</span>
                    <span className="font-medium">{company.attendance_rate}%</span>
                  </div>
                  <Progress value={company.attendance_rate} className="h-2" />
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 pt-2">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-emerald-600">
                      <UserCheck className="h-4 w-4" />
                      <span className="font-semibold">{company.present_today}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Present</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-red-600">
                      <UserX className="h-4 w-4" />
                      <span className="font-semibold">{company.absent_today}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Absent</p>
                  </div>
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 text-blue-600">
                      <Calendar className="h-4 w-4" />
                      <span className="font-semibold">{company.on_leave_today}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">On Leave</p>
                  </div>
                </div>

                {/* Footer Stats */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Staff: </span>
                    <span className="font-medium">{company.total_employees}</span>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">OT Today: </span>
                    <span className="font-medium">{company.total_overtime_hours}h</span>
                  </div>
                  <Link to={`/app/owner/company/${company.company_id}`}>
                    <Button variant="ghost" size="sm" className="gap-1">
                      Details
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Exception Widgets Section */}
      <h2 className="text-xl font-semibold mb-4 mt-8" style={{ fontFamily: 'Plus Jakarta Sans' }}>
        Workforce Exceptions
      </h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Lates Widget */}
        <Card data-testid="top-lates-widget">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-amber-500" />
              Top Lates
            </CardTitle>
            <CardDescription>Employees with most late arrivals this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {widgets?.top_lates?.slice(0, 5).map((emp, idx) => (
                <div key={emp.employee_id} className="flex items-center justify-between p-2 bg-secondary/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="w-6 h-6 flex items-center justify-center p-0">
                      {idx + 1}
                    </Badge>
                    <div>
                      <p className="font-medium text-sm">{emp.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{emp.company_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-amber-600">{emp.late_count}x</p>
                    <p className="text-xs text-muted-foreground">{emp.total_late_minutes}min total</p>
                  </div>
                </div>
              ))}
              {(!widgets?.top_lates || widgets.top_lates.length === 0) && (
                <p className="text-muted-foreground text-sm text-center py-4">No late arrivals recorded</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Overworked Employees Widget */}
        <Card data-testid="overworked-widget">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Overworked Employees
            </CardTitle>
            <CardDescription>Employees exceeding weekly OT threshold (10h+)</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {widgets?.overworked_employees?.slice(0, 5).map((emp, idx) => (
                <div key={emp.employee_id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <div>
                      <p className="font-medium text-sm">{emp.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{emp.company_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">{emp.weekly_avg_ot_hours}h</p>
                    <p className="text-xs text-muted-foreground">this week</p>
                  </div>
                </div>
              ))}
              {(!widgets?.overworked_employees || widgets.overworked_employees.length === 0) && (
                <div className="text-center py-4">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">No overworked employees</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Late Clock-out without OT Widget */}
        <Card data-testid="late-clockout-widget">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Hourglass className="h-5 w-5 text-purple-500" />
              Late Clock-out without OT Request
            </CardTitle>
            <CardDescription>Employees clocking out late without pre-approved OT</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {widgets?.late_clockout_no_ot?.slice(0, 5).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-purple-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <XCircle className="h-5 w-5 text-purple-500" />
                    <div>
                      <p className="font-medium text-sm">{item.employee_name}</p>
                      <p className="text-xs text-muted-foreground">{item.company_name} • {item.date}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-purple-600">{item.ot_minutes}min</p>
                    <p className="text-xs text-muted-foreground">unapproved</p>
                  </div>
                </div>
              ))}
              {(!widgets?.late_clockout_no_ot || widgets.late_clockout_no_ot.length === 0) && (
                <div className="text-center py-4">
                  <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground text-sm">All late clock-outs have OT approval</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* OT Approved vs Unapproved */}
        <Card data-testid="ot-stats-widget">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Timer className="h-5 w-5 text-blue-500" />
              OT Approval Status
            </CardTitle>
            <CardDescription>Breakdown of overtime hours by approval status</CardDescription>
          </CardHeader>
          <CardContent>
            {widgets?.ot_approved_vs_unapproved && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="p-3 bg-green-50 rounded-lg">
                    <CheckCircle className="h-6 w-6 text-green-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-green-700">
                      {widgets.ot_approved_vs_unapproved.approved_hours || 0}h
                    </p>
                    <p className="text-xs text-muted-foreground">Approved</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <Clock className="h-6 w-6 text-amber-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-amber-700">
                      {widgets.ot_approved_vs_unapproved.pending_hours || 0}h
                    </p>
                    <p className="text-xs text-muted-foreground">Pending</p>
                  </div>
                  <div className="p-3 bg-red-50 rounded-lg">
                    <XCircle className="h-6 w-6 text-red-500 mx-auto mb-1" />
                    <p className="text-lg font-bold text-red-700">
                      {widgets.ot_approved_vs_unapproved.unapproved_hours || 0}h
                    </p>
                    <p className="text-xs text-muted-foreground">Unapproved</p>
                  </div>
                </div>
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground text-center">
                    Total OT this period: <span className="font-semibold">{
                      ((widgets.ot_approved_vs_unapproved.approved_hours || 0) + 
                       (widgets.ot_approved_vs_unapproved.pending_hours || 0) + 
                       (widgets.ot_approved_vs_unapproved.unapproved_hours || 0)).toFixed(1)
                    }h</span>
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payroll Forecast Section */}
      <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Plus Jakarta Sans' }}>
        Payroll Forecast
      </h2>
      
      <Card className="mb-8" data-testid="payroll-forecast">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-500" />
                Payroll Forecast
              </CardTitle>
              <CardDescription>Projected payroll based on headcount, approved OT, and holidays</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Period:</span>
              <Input
                type="month"
                value={forecastPeriod}
                onChange={(e) => setForecastPeriod(e.target.value)}
                className="w-[160px]"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {forecast && (
            <>
              {/* Totals Summary */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-6">
                <Card className="bg-secondary/30">
                  <CardContent className="pt-4 pb-2 text-center">
                    <p className="text-2xl font-bold">{forecast.totals?.total_headcount || 0}</p>
                    <p className="text-xs text-muted-foreground">Total Headcount</p>
                  </CardContent>
                </Card>
                <Card className="bg-secondary/30">
                  <CardContent className="pt-4 pb-2 text-center">
                    <p className="text-xl font-bold">{formatCurrency(forecast.totals?.total_base_salary)}</p>
                    <p className="text-xs text-muted-foreground">Base Salary</p>
                  </CardContent>
                </Card>
                <Card className="bg-secondary/30">
                  <CardContent className="pt-4 pb-2 text-center">
                    <p className="text-xl font-bold">{formatCurrency(forecast.totals?.total_approved_ot_cost)}</p>
                    <p className="text-xs text-muted-foreground">Approved OT</p>
                  </CardContent>
                </Card>
                <Card className="bg-secondary/30">
                  <CardContent className="pt-4 pb-2 text-center">
                    <p className="text-xl font-bold">{formatCurrency(forecast.totals?.total_holiday_pay)}</p>
                    <p className="text-xs text-muted-foreground">Holiday Pay</p>
                  </CardContent>
                </Card>
                <Card className="bg-green-50 border-green-200">
                  <CardContent className="pt-4 pb-2 text-center">
                    <p className="text-xl font-bold text-green-700">{formatCurrency(forecast.totals?.total_forecast_net)}</p>
                    <p className="text-xs text-green-600">Net Forecast</p>
                  </CardContent>
                </Card>
              </div>

              {/* Company Breakdown Table */}
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle px-4 sm:px-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Company</TableHead>
                        <TableHead className="text-right">Headcount</TableHead>
                        <TableHead className="text-right hidden sm:table-cell">Base Salary</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Approved OT</TableHead>
                        <TableHead className="text-right hidden lg:table-cell">Holidays</TableHead>
                        <TableHead className="text-right hidden lg:table-cell">Deductions</TableHead>
                        <TableHead className="text-right">Net Forecast</TableHead>
                        <TableHead className="text-right hidden md:table-cell">vs Historical</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {forecast.forecasts?.map((company) => (
                    <TableRow key={company.company_id}>
                      <TableCell className="font-medium">{company.company_name}</TableCell>
                      <TableCell className="text-right">{company.scheduled_headcount}</TableCell>
                      <TableCell className="text-right hidden sm:table-cell">{formatCurrency(company.base_salary_total)}</TableCell>
                      <TableCell className="text-right hidden md:table-cell">
                        <span className="text-blue-600">{company.approved_ot_hours}h</span>
                        <span className="text-muted-foreground text-xs ml-1">
                          ({formatCurrency(company.approved_ot_cost)})
                        </span>
                      </TableCell>
                      <TableCell className="text-right hidden lg:table-cell">
                        {company.holiday_count > 0 ? (
                          <Badge variant="outline">{company.holiday_count} days</Badge>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right text-red-600 hidden lg:table-cell">
                        -{formatCurrency(company.estimated_deductions)}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-green-600">
                        {formatCurrency(company.forecast_net_payroll)}
                      </TableCell>
                      <TableCell className="text-right hidden md:table-cell">
                        {company.variance_from_historical !== null ? (
                          <div className="flex items-center justify-end gap-1">
                            {company.variance_from_historical > 0 ? (
                              <>
                                <ArrowUp className="h-4 w-4 text-red-500" />
                                <span className="text-red-500 text-sm">
                                  +{formatCurrency(company.variance_from_historical)}
                                </span>
                              </>
                            ) : (
                              <>
                                <ArrowDown className="h-4 w-4 text-green-500" />
                                <span className="text-green-500 text-sm">
                                  {formatCurrency(company.variance_from_historical)}
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-xs">No history</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

              <p className="text-xs text-muted-foreground mt-4 text-center">
                {forecast.note}
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default OwnerDashboard;
